import React from 'react'
import {
  Button,
  ButtonGroup
} from 'react-bootstrap'
import { FormattedMessage } from 'react-intl'
import { REQUEST_UPLOAD_TEMPLATE_START } from '../sagas'
import { runSaga } from 'store/sagaMiddleware'
import { call, put, take, spawn, actionChannel, delay } from 'redux-saga/effects'
import { action as apiAction } from 'api/Api'
import {
  add as addToast,
  set as setToast,
  remove as removeToast
} from 'store/redux/toasts'

const $ = window.$

function * delayedRemoveToast (uploadId) {
  yield delay(2000)

  yield put(removeToast({
    id: 'upload-' + uploadId
  }))
}

function createUploadObject (uploadUrl, file, onUpload) {
  return {
    uploadUrl,
    file,
    onUpload,
    currentByte: 0,
    chunkSize: 10240,
    minChunkSize: 32, // 32B
    maxChunkSize: 2 * 1024 * 1024, // 2MB, modify depending on limit
    lastLoadedByte: 0,
    targetDuration: 1000,
    chunkStartDate: null,
    chunkEndDate: null
  }
}

function adjustChunkSize (uploadObject) {
  const {
    chunkStartDate,
    chunkEndDate,
    currentByte,
    lastLoadedByte,
    chunkSize,
    minChunkSize,
    maxChunkSize,
    targetDuration
  } = uploadObject

  const duration = chunkEndDate - chunkStartDate
  const loadedSize = lastLoadedByte - currentByte
  uploadObject.chunkSize = Math.floor(loadedSize * (targetDuration / duration))
  if (chunkSize < minChunkSize) uploadObject.chunkSize = minChunkSize
  if (chunkSize > maxChunkSize) uploadObject.chunkSize = maxChunkSize
}

function readChunk (uploadObject) {
  const {
    currentByte,
    chunkSize,
    file
  } = uploadObject

  let end = currentByte + chunkSize
  end = end > file.size ? file.size : end

  uploadObject.lastLoadedByte = end

  return new Promise((resolve, reject) => {
    const blob = file.slice(currentByte, end)
    const reader = new FileReader()
    reader.onloadend = function (event) {
      if (event.target.readyState === FileReader.DONE) {
        resolve(event.target.result)
      }
    }
    reader.readAsArrayBuffer(blob)
  })
}

function * loadChunk (uploadObject) {
  const chunk = yield call(readChunk, uploadObject)

  yield call(sendChunk, chunk, uploadObject)

  yield chunkSent(uploadObject)
}

function sendChunk (chunk, uploadObject) {
  const {
    uploadUrl
  } = uploadObject

  uploadObject.chunkStartDate = new Date()

  return new Promise((resolve, reject) => {
    $.ajax({
      url: uploadUrl,
      data: chunk,
      type: 'POST',
      processData: false,
      cache: false
    }).done(function () {
      resolve()
    }).fail(function () {
      reject()
    })
  })
}

function * chunkSent (uploadObject) {
  const {
    file,
    lastLoadedByte,
    currentByte,
    uploadId
  } = uploadObject

  uploadObject.chunkEndDate = new Date()

  adjustChunkSize(uploadObject)

  uploadObject.currentByte = lastLoadedByte

  const progress = Math.floor(currentByte / file.size * 100) || 0

  yield put(setToast({
    id: 'upload-' + uploadId,
    progress
  }))

  if (currentByte < file.size) {
    yield loadChunk(uploadObject)
  } else {
    yield finalize(uploadObject)
  }
}

function * finalize (uploadObject) {
  const {
    uploadUrl,
    onUpload
  } = uploadObject

  const { FileId } = yield call(apiAction, 'EndUploadTemplate', {
    uploadUrl
  })

  yield put(setToast({
    id: 'upload-' + uploadUrl,
    type: 'success',
    progress: 100
  }))

  yield spawn(delayedRemoveToast, uploadUrl)

  if (onUpload) {
    yield onUpload({
      fileId: FileId
    })
  }
}

function * uploadTemplateHandler (action) {
  const { file, onUpload } = action.payload

  try {
    const { UploadUrl: uploadUrl } = yield call(apiAction, 'StartUploadTemplate', {
      size: file.size
    })

    yield put(addToast({
      id: 'upload-' + uploadUrl,
      message: (
        <FormattedMessage
          id='UPLOAD_FILE_PROGRESS_RUNNING'
          values={{
            fileName: <b>{file.name}</b>
          }}
        />
      ),
      progress: 0
    }))

    try {
      const uploadObject = createUploadObject(uploadUrl, file, onUpload)

      yield loadChunk(uploadObject)
    } catch (error) {
      yield put(removeToast({
        id: 'upload-' + uploadUrl
      }))

      throw error
    }
  } catch (error) {
    const id = 'uploadError-' + Date.now()

    yield put(addToast({
      id,
      type: 'warning',
      message: (
        <div>
          <div>
            <FormattedMessage
              id='UPLOAD_FILE_ERROR'
              values={{
                fileName: <b>{file.name}</b>
              }}
            />
          </div>
          <div>
            <ButtonGroup style={{ marginTop: 5 }}>
              <Button
                bsStyle='primary'
                bsSize='small'
                onClick={() => runSaga(function * () {
                  yield put(removeToast({
                    id
                  }))

                  yield put({
                    type: REQUEST_UPLOAD_TEMPLATE_START,
                    payload: {
                      ...action.payload
                    }
                  })
                })}
              >
                <FormattedMessage id='UPLOAD_RETRY' />
              </Button>
              <Button
                bsSize='small'
                onClick={() => runSaga(function * () {
                  yield put(removeToast({
                    id
                  }))
                })}
              >
                <FormattedMessage id='CANCEL' />
              </Button>
            </ButtonGroup>
          </div>
        </div>
      )
    }))
  }
}

export default function * uploadTemplateListener () {
  console.log('uploadTemplateListener has been started')

  // 1- Create a channel for request actions
  const requestChan = yield actionChannel(REQUEST_UPLOAD_TEMPLATE_START)
  while (true) {
    // 2- take from the channel
    const action = yield take(requestChan)
    // 3- Note that we're using a blocking call
    yield call(uploadTemplateHandler, action)
  }
}
